/**
 * «Κατάλογος» (Φάση 3, έγκριση ιδιοκτήτη 12/10/2026): τύποι, μετατροπές και σώμα αποθήκευσης.
 *
 * Η φόρμα προϊόντος είναι ΑΚΡΙΒΩΣ η φόρμα του παλιού παραθύρου (ProductManagement): ίδια πεδία,
 * ίδιες μετατροπές στο άνοιγμα (openEditProduct) και ίδιο σώμα POST/PUT, ώστε ο server να μη δει
 * καμία διαφορά. Η τιμή ζει μόνο στα μεγέθη (Φ5)· κανένα πεδίο τιμής προϊόντος.
 * Μία εξαίρεση, για να μη χάνονται χρήματα: η δική του τιμή έξτρα του υλικού (βλ. ingredientFromApi).
 */
import { isFeeProduct } from '../../utils/pricing';

export type ProductType = 'ΕΜΠΟΡΕΥΜΑ' | 'ΠΡΟΙΟΝ' | 'ΥΠΗΡΕΣΙΑ';
export const PRODUCT_TYPES: ProductType[] = ['ΕΜΠΟΡΕΥΜΑ', 'ΠΡΟΙΟΝ', 'ΥΠΗΡΕΣΙΑ'];

/** Όνομα του μεγέθους «χωρίς μέγεθος» — ίδιο με το BASE_SIZE_NAME του backend. */
export const BASE_SIZE_NAME = 'Κανονικό';

/* ───────────────────────── Ό,τι στέλνει ο server ───────────────────────── */

export interface ApiCategory {
  id: number;
  name: string;
  description?: string | null;
  display_order: number;
  product_count?: number | string;
  is_active?: boolean;
  is_hidden?: boolean;
  is_system?: boolean;
  parent_id?: number | null;
  subcategories?: ApiCategory[];
  default_product_type?: ProductType | null;
  default_vat?: number | string | null;
  color?: string | null;
}

export interface ApiSize {
  id?: number;
  name: string;
  price_modifier: number | string;
  price_modifier_take_away?: number | string | null;
  price_modifier_delivery?: number | string | null;
  price_modifier_dine_in?: number | string | null;
  is_default?: boolean | null;
  is_available?: boolean | null;
  is_hidden?: boolean | null;
  display_order?: number | null;
}

export interface ApiSimpleIngredient {
  name: string;
  extra_price?: number | string | null;
  is_default?: boolean | null;
  is_removable?: boolean | null;
  category?: string | null;
  store_ingredient_id?: number | null;
  extra_price_override?: number | string | null;
  is_extra_available?: boolean | null;
}

export interface ApiProduct {
  id: number;
  category_id: number;
  category_name?: string;
  name: string;
  description?: string | null;
  vat: number | string;
  vat_take_away?: number | string | null;
  vat_delivery?: number | string | null;
  vat_dine_in?: number | string | null;
  product_type?: string | null;
  is_available: boolean;
  is_hidden?: boolean | null;
  is_kiosk_recommended?: boolean | null;
  is_weight_variable?: boolean | null;
  is_open_price?: boolean | null;
  barcode?: string | null;
  barcode_type?: 'standard' | 'weighted' | null;
  sell_unit?: 'piece' | 'kg' | 'g' | null;
  sizes?: ApiSize[] | null;
  ingredients?: Array<{ id: number; is_removable?: boolean }> | null;
  simple_ingredients?: ApiSimpleIngredient[] | null;
  display_order?: number | null;
  supplier_id?: number | null;
  material_code?: string | null;
  purchase_price?: number | string | null;
  min_stock_level?: number | string | null;
  current_stock?: number | string | null;
  can_be_ingredient?: boolean | null;
  stock_unit?: string | null;
  stock_unit_quantity?: number | string | null;
  max_discount_percent?: number | string | null;
  fee_category?: number | null;
  auto_plastic_fee_count?: number | string | null;
  fee_unit_gross?: number | string | null;
  color?: string | null;
}

export interface ApiOptionChoice {
  id?: number;
  name: string;
  price_modifier: number | string;
  is_default?: boolean;
}

export interface ApiOptionGroup {
  id: number;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  display_order?: number;
  choices?: ApiOptionChoice[];
  /** σε πόσα προϊόντα είναι (Κ3)· λείπει σε παλιό backend */
  product_count?: number | string;
}

export interface ApiSizeGroup {
  id: number;
  name: string;
  items?: Array<{ id?: number; name: string; default_price_modifier?: number | string; price_modifier?: number | string; is_default?: boolean }>;
  product_count?: number | string;
}

export interface ApiStoreIngredient {
  id: number;
  name: string;
  extra_price: number | string;
  category?: string | null;
  display_order?: number;
  is_available: boolean;
  product_count?: number | string;
}

export interface VatCategory { id: number; rate: number | string; name?: string }

/* ───────────────────────── Αναφορές (και σε πρόχειρα) ───────────────────────── */

/** Κατηγορία: id · 0 = «Χωρίς κατηγορία» (η κρυφή system του καταστήματος) · 'nc:N' = νέα, πρόχειρη. */
export type CatRef = number | string;
/** Ομάδα επιλογών: id · 'ng:N' = νέα, πρόχειρη. */
export type GroupRef = number | string;
/** Υλικό βιβλιοθήκης: id · 'ni:N' = νέο, πρόχειρο. */
export type IngRef = number | string;

export const productKey = (id: number) => `p:${id}`;
export const categoryKey = (id: number) => `c:${id}`;
/** Το κλειδί κόμβου μιας κατηγορίας της φόρμας (0 → «none»). */
export const catKeyOfRef = (ref: CatRef): string => (typeof ref === 'string' ? ref : ref > 0 ? categoryKey(ref) : 'none');
/** Η αναφορά της φόρμας για ένα κλειδί κόμβου ('c:5' → 5, 'nc:1' → 'nc:1', ό,τι άλλο → 0). */
export const refOfCatKey = (key: string): CatRef => (key.startsWith('c:') ? Number(key.slice(2)) : key.startsWith('nc:') ? key : 0);

/* ───────────────────────── Αριθμοί ───────────────────────── */

/** Τιμή καναλιού: κενό/άκυρο = null (= η «Τιμή»), αλλιώς αριθμός. */
export function channelValue(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Λεπτά — η σύγκριση ποσών γίνεται εδώ, όχι με δεκαδικά. */
export const cents = (v: number | null | undefined): number | null => (v === null || v === undefined || !Number.isFinite(v) ? null : Math.round(v * 100));

export const sameMoney = (a: number | null | undefined, b: number | null | undefined) => cents(a) === cents(b);

/** «3,40» / «3.40» → 3.4· κενό ή άκυρο → null. */
export function parseDecimal(text: string): number | null {
  const s = String(text ?? '').replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

/** «3,40» — για πεδία (χωρίς «€»). */
export const fmtDecimal = (n: number | null | undefined) => (n === null || n === undefined || !Number.isFinite(n) ? '' : n.toFixed(2).replace('.', ','));

/** Κλειδί αναζήτησης: χωρίς τόνους και κεφαλαία («ΚΟΥΛΟΎΡΙ» = «κουλουρι»), τελικό σ = σ. */
export const searchKey = (s: string | null | undefined) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('el').replace(/ς/g, 'σ');

/* ───────────────────────── Φόρμα προϊόντος ───────────────────────── */

export interface FormSize {
  /** σταθερό κλειδί γραμμής (σύγκριση/σύρσιμο)· δεν στέλνεται */
  _uid: string;
  id?: number;
  name: string;
  price_modifier: number;
  price_modifier_take_away: number | null;
  price_modifier_delivery: number | null;
  price_modifier_dine_in: number | null;
  is_default: boolean;
  is_available: boolean;
  is_hidden?: boolean;
}

export interface FormIngredient {
  _uid: string;
  store_ingredient_id: IngRef;
  name: string;
  category: string;
  /** η τιμή έξτρα της βιβλιοθήκης — το «κενό» της στήλης «Έξτρα» */
  library_price: number;
  /** δική του τιμή έξτρα· null = της βιβλιοθήκης */
  extra_price_override: number | null;
  is_default: boolean;
  is_removable: boolean;
}

/** Η φόρμα του παλιού παραθύρου (ProductFormData), με αναφορές που επιτρέπουν και πρόχειρα. */
export interface ProductForm {
  category_id: CatRef;
  name: string;
  description: string;
  vat: number;
  vat_take_away?: number;
  vat_delivery?: number;
  vat_dine_in?: number;
  product_type: string;
  is_weight_variable: boolean;
  is_open_price: boolean;
  barcode: string;
  barcode_type: 'standard' | 'weighted';
  sell_unit: 'piece' | 'kg' | 'g';
  sizes: FormSize[];
  ingredients: { ingredient_id: number; is_removable: boolean }[];
  simple_ingredients: FormIngredient[];
  supplier_id?: number;
  material_code?: string;
  purchase_price?: number;
  min_stock_level?: number;
  current_stock?: number;
  can_be_ingredient: boolean;
  stock_unit: string;
  stock_unit_quantity?: number;
  max_discount_percent: number;
  fee_category: number | null;
  auto_plastic_fee_count: number;
  color: string | null;
}

let uidSeq = 0;
/** Νέο κλειδί γραμμής για μέγεθος/υλικό που φτιάχνεται στη φόρμα. */
export const newUid = (prefix: string) => `${prefix}${++uidSeq}`;

export function newSize(name = '', isDefault = false): FormSize {
  return {
    _uid: newUid('ns'), name, price_modifier: 0,
    price_modifier_take_away: null, price_modifier_delivery: null, price_modifier_dine_in: null,
    is_default: isDefault, is_available: true, is_hidden: false,
  };
}

/** Μέγεθος χωρίς κλειδί γραμμής (π.χ. από το applyFeeMode) παίρνει νέο. */
export function ensureSizeUids<T extends { _uid?: string }>(sizes: T[]): Array<T & { _uid: string }> {
  return sizes.map(s => (s._uid ? s : { ...s, _uid: newUid('ns') })) as Array<T & { _uid: string }>;
}

export function sizeFromApi(s: ApiSize, fallbackUid: string): FormSize {
  return {
    _uid: s.id != null ? `s${s.id}` : fallbackUid,
    id: s.id,
    name: s.name,
    price_modifier: Number(s.price_modifier) || 0,
    price_modifier_take_away: channelValue(s.price_modifier_take_away),
    price_modifier_delivery: channelValue(s.price_modifier_delivery),
    price_modifier_dine_in: channelValue(s.price_modifier_dine_in),
    is_default: s.is_default === true,
    is_available: s.is_available !== false,
    is_hidden: s.is_hidden === true,
  };
}

/**
 * Υλικό όπως το στέλνει η λίστα (extra_price = COALESCE(δική του, βιβλιοθήκης)). Η λίστα ΔΕΝ στέλνει
 * τη δική του τιμή ξεχωριστά· το παλιό παράθυρο την έχανε σε κάθε αποθήκευση (extra_price_override
 * κενό → null). Εδώ ξαναβγαίνει από τη διαφορά με τη βιβλιοθήκη, ώστε η αποθήκευση να μην αλλάζει
 * ποσό που ο χειριστής δεν άγγιξε.
 */
export function ingredientFromApi(si: ApiSimpleIngredient, library: Map<number, ApiStoreIngredient>): FormIngredient {
  const id = Number(si.store_ingredient_id) || 0;
  const lib = library.get(id);
  const effective = Number(si.extra_price) || 0;
  const libraryPrice = lib ? Number(lib.extra_price) || 0 : effective;
  return {
    _uid: `i${id}`,
    store_ingredient_id: id,
    name: si.name,
    category: si.category || '',
    library_price: libraryPrice,
    extra_price_override: lib && !sameMoney(effective, libraryPrice) ? effective : null,
    is_default: si.is_default !== false,
    is_removable: si.is_removable !== false,
  };
}

export interface FormContext {
  /** ids των κρυφών «system» κατηγοριών (= «Χωρίς κατηγορία») */
  systemIds: Set<number>;
  library: Map<number, ApiStoreIngredient>;
}

/** Το openEditProduct του παλιού παραθύρου. */
export function formFromProduct(p: ApiProduct, ctx: FormContext): ProductForm {
  const hasSizes = (p.sizes?.length || 0) > 0;
  return {
    category_id: ctx.systemIds.has(p.category_id) ? 0 : p.category_id,
    name: p.name,
    description: p.description || '',
    vat: Number(p.vat),
    vat_take_away: p.vat_take_away ? Number(p.vat_take_away) : undefined,
    vat_delivery: p.vat_delivery ? Number(p.vat_delivery) : undefined,
    vat_dine_in: p.vat_dine_in ? Number(p.vat_dine_in) : undefined,
    product_type: p.product_type || 'ΕΜΠΟΡΕΥΜΑ',
    is_weight_variable: p.is_weight_variable || false,
    is_open_price: p.is_open_price || false,
    barcode: p.barcode || '',
    barcode_type: p.barcode_type || 'standard',
    sell_unit: p.sell_unit || 'piece',
    // Μη-τέλος χωρίς μέγεθος (ανωμαλία μετά τη Φ4): ανοίγει με «Κανονικό» για να πάρει τιμή·
    // το τέλος μένει χωρίς μεγέθη.
    sizes: hasSizes || isFeeProduct(p)
      ? (p.sizes || []).map((s, i) => sizeFromApi(s, `p${p.id}-${i}`))
      : [{ ...newSize(BASE_SIZE_NAME, true), _uid: `p${p.id}-base` }],
    ingredients: (p.ingredients || []).map(ing => ({ ingredient_id: ing.id, is_removable: ing.is_removable !== false })),
    simple_ingredients: (p.simple_ingredients || []).map(si => ingredientFromApi(si, ctx.library)),
    supplier_id: p.supplier_id ?? undefined,
    material_code: p.material_code ?? undefined,
    purchase_price: p.purchase_price ? Number(p.purchase_price) : undefined,
    min_stock_level: p.min_stock_level ? Number(p.min_stock_level) : undefined,
    current_stock: p.current_stock ? Number(p.current_stock) : undefined,
    can_be_ingredient: p.can_be_ingredient || false,
    stock_unit: p.stock_unit || '',
    stock_unit_quantity: p.stock_unit_quantity ? Number(p.stock_unit_quantity) : undefined,
    max_discount_percent: p.max_discount_percent !== undefined && p.max_discount_percent !== null ? Number(p.max_discount_percent) : 100,
    fee_category: p.fee_category !== undefined && p.fee_category !== null ? Number(p.fee_category) : null,
    auto_plastic_fee_count: p.auto_plastic_fee_count !== undefined && p.auto_plastic_fee_count !== null ? Number(p.auto_plastic_fee_count) : 0,
    color: p.color ?? null,
  };
}

/** Προεπιλογές της κατηγορίας για ΝΕΟ προϊόν (default_product_type / default_vat). */
export interface CategoryDefaults {
  default_product_type?: string | null;
  default_vat?: number | string | null;
}

/** Το resetProductForm του παλιού παραθύρου: οι προεπιλογές της κατηγορίας μπαίνουν αμέσως. */
export function emptyProductForm(category: CatRef, defaults?: CategoryDefaults | null): ProductForm {
  return {
    category_id: category,
    name: '',
    description: '',
    vat: defaults?.default_vat !== undefined && defaults?.default_vat !== null && defaults?.default_vat !== '' ? Number(defaults.default_vat) : 24,
    product_type: defaults?.default_product_type || 'ΕΜΠΟΡΕΥΜΑ',
    is_weight_variable: false,
    is_open_price: false,
    barcode: '',
    barcode_type: 'standard',
    sell_unit: 'piece',
    sizes: [newSize(BASE_SIZE_NAME, true)],
    ingredients: [],
    simple_ingredients: [],
    can_be_ingredient: false,
    stock_unit: '',
    max_discount_percent: 100,
    fee_category: null,
    auto_plastic_fee_count: 0,
    color: null,
  };
}

export interface PayloadRefs {
  /** πραγματικό id κατηγορίας (0 = χωρίς κατηγορία) */
  categoryId: (ref: CatRef) => number;
  /** πραγματικό id υλικού βιβλιοθήκης */
  ingredientId: (ref: IngRef) => number;
}

export function sizePayload(s: FormSize): Record<string, unknown> {
  return {
    id: s.id,
    name: s.name,
    price_modifier: s.price_modifier,
    price_modifier_take_away: s.price_modifier_take_away,
    price_modifier_delivery: s.price_modifier_delivery,
    price_modifier_dine_in: s.price_modifier_dine_in,
    is_default: s.is_default,
    is_available: s.is_available,
    is_hidden: s.is_hidden === true,
  };
}

export function ingredientPayload(i: FormIngredient, refs: PayloadRefs): Record<string, unknown> {
  return {
    name: i.name,
    extra_price: i.extra_price_override ?? i.library_price,
    is_default: i.is_default,
    is_removable: i.is_removable,
    category: i.category,
    store_ingredient_id: refs.ingredientId(i.store_ingredient_id),
    extra_price_override: i.extra_price_override,
  };
}

/** Σώμα POST/PUT προϊόντος — τα πεδία του παλιού productForm, τίποτα παραπάνω. */
export function productPayload(form: ProductForm, refs: PayloadRefs): Record<string, unknown> {
  return {
    category_id: refs.categoryId(form.category_id),
    name: form.name,
    description: form.description,
    vat: form.vat,
    vat_take_away: form.vat_take_away,
    vat_delivery: form.vat_delivery,
    vat_dine_in: form.vat_dine_in,
    product_type: form.product_type,
    is_weight_variable: form.is_weight_variable,
    is_open_price: form.is_open_price,
    barcode: form.barcode,
    barcode_type: form.barcode_type,
    sell_unit: form.sell_unit,
    sizes: form.sizes.map(sizePayload),
    ingredients: form.ingredients,
    simple_ingredients: form.simple_ingredients.map(i => ingredientPayload(i, refs)),
    supplier_id: form.supplier_id,
    material_code: form.material_code,
    purchase_price: form.purchase_price,
    min_stock_level: form.min_stock_level,
    current_stock: form.current_stock,
    can_be_ingredient: form.can_be_ingredient,
    stock_unit: form.stock_unit,
    stock_unit_quantity: form.stock_unit_quantity,
    max_discount_percent: form.max_discount_percent,
    fee_category: form.fee_category,
    auto_plastic_fee_count: form.auto_plastic_fee_count,
    color: form.color,
  };
}

/** Ένα προεπιλεγμένο μέγεθος: όποιο σημειώθηκε, τα υπόλοιπα όχι (updateSize του παλιού). */
export function withDefaultSize(sizes: FormSize[], uid: string): FormSize[] {
  return sizes.map(s => ({ ...s, is_default: s._uid === uid }));
}

/** Ε10α: η τιμή μέλους ομάδας μεγεθών είναι η ΤΕΛΙΚΗ τιμή του μεγέθους, αυτούσια. */
export function sizesFromGroup(group: ApiSizeGroup): FormSize[] {
  return (group.items || []).map(item => ({
    ...newSize(item.name, item.is_default === true),
    price_modifier: Number(item.default_price_modifier ?? item.price_modifier) || 0,
  }));
}

/* ───────────────────────── Κατηγορίες ───────────────────────── */

export interface CategoryForm {
  name: string;
  description: string;
  default_product_type: '' | ProductType;
  default_vat: number | null;
  is_hidden: boolean;
  color: string | null;
}

export function categoryFormFromApi(c: ApiCategory): CategoryForm {
  return {
    name: c.name,
    description: c.description || '',
    default_product_type: (c.default_product_type || '') as CategoryForm['default_product_type'],
    default_vat: c.default_vat !== undefined && c.default_vat !== null && c.default_vat !== '' ? Number(c.default_vat) : null,
    is_hidden: c.is_hidden === true,
    color: c.color ?? null,
  };
}

export const EMPTY_CATEGORY_FORM: CategoryForm = {
  name: '', description: '', default_product_type: '', default_vat: null, is_hidden: false, color: null,
};

/** Σώμα POST/PUT κατηγορίας — τα πεδία του παλιού categoryForm. */
export function categoryPayload(form: CategoryForm, parentId: number | null, displayOrder: number): Record<string, unknown> {
  return {
    name: form.name,
    description: form.description,
    display_order: displayOrder,
    parent_id: parentId,
    default_product_type: form.default_product_type,
    default_vat: form.default_vat,
    is_hidden: form.is_hidden,
    color: form.color,
  };
}

/** Ένα επίπεδο υποκατηγοριών, όπως σήμερα: ρίζες + παιδιά ανά ρίζα (κλειδιά 'c:ID' / 'nc:N'). */
export interface TreeShape {
  roots: string[];
  children: Record<string, string[]>;
}

export interface ServerCategory {
  key: string;
  id: number;
  parentKey: string | null;
  display_order: number;
  form: CategoryForm;
}

/** Οι κατηγορίες του καταστήματος: δέντρο στη σειρά του API, χωρίς τις system. */
export function buildCategories(list: ApiCategory[]): { categories: Record<string, ServerCategory>; tree: TreeShape; systemIds: Set<number> } {
  const categories: Record<string, ServerCategory> = {};
  const tree: TreeShape = { roots: [], children: {} };
  const systemIds = new Set<number>();
  const add = (c: ApiCategory, parentKey: string | null) => {
    const key = categoryKey(c.id);
    categories[key] = { key, id: c.id, parentKey, display_order: Number(c.display_order) || 0, form: categoryFormFromApi(c) };
    if (parentKey) (tree.children[parentKey] = tree.children[parentKey] || []).push(key);
    else tree.roots.push(key);
  };
  list.forEach(c => {
    if (c.is_system) { systemIds.add(c.id); return; }
    add(c, null);
    (c.subcategories || []).forEach(sub => {
      if (sub.is_system) { systemIds.add(sub.id); return; }
      add(sub, categoryKey(c.id));
    });
  });
  return { categories, tree, systemIds };
}

export const cloneTree = (t: TreeShape): TreeShape => ({
  roots: t.roots.slice(),
  children: Object.keys(t.children).reduce<Record<string, string[]>>((acc, k) => { acc[k] = t.children[k].slice(); return acc; }, {}),
});

export function parentOf(tree: TreeShape, key: string): string | null {
  const keys = Object.keys(tree.children);
  for (let i = 0; i < keys.length; i += 1) if (tree.children[keys[i]].includes(key)) return keys[i];
  return null;
}

/** Μετακινεί κατηγορία κάτω από άλλη (ή στη ρίζα) στη θέση index. Κατηγορία με υποκατηγορίες μένει στη ρίζα. */
export function moveInTree(tree: TreeShape, key: string, parentKey: string | null, index: number): TreeShape {
  if (parentKey === key) return tree;
  if (parentKey && (tree.children[key] || []).length > 0) return tree;
  if (parentKey && parentOf(tree, parentKey)) return tree;
  const next = cloneTree(tree);
  next.roots = next.roots.filter(k => k !== key);
  Object.keys(next.children).forEach(p => { next.children[p] = next.children[p].filter(k => k !== key); });
  const list = parentKey ? (next.children[parentKey] = next.children[parentKey] || []) : next.roots;
  list.splice(Math.max(0, Math.min(index, list.length)), 0, key);
  return next;
}

/** Όλα τα κλειδιά του δέντρου (ρίζες και παιδιά), στη σειρά εμφάνισης. */
export function treeKeys(tree: TreeShape): string[] {
  return tree.roots.flatMap(k => [k, ...(tree.children[k] || [])]);
}
